import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, SendIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
const WEB3FORMS_ACCESS_KEY = '9f6d2e40-eb5c-4438-9a46-6e175eae72f4';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    inquiryType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `[AU Contact] ${formData.inquiryType || 'General'} — ${formData.subject}`,
        from_name: formData.name,
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        inquiry_type: formData.inquiryType,
        message_subject: formData.subject,
        message: formData.message,
        botcheck: ''
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          organization: '',
          subject: '',
          inquiryType: '',
          message: ''
        });
      } else {
        setError(data.message || 'Something went wrong. Please try again or email us directly.');
      }
    } catch (err: any) {
      setError(err?.message || 'Could not send your message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const contactInfo = [{
    icon: MapPinIcon,
    title: 'Office Address',
    lines: ['African Union Commission', 'Liberia Road', 'Ambassadorial Enclave', 'Accra, Ghana']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    lines: ['+233 50 753 7362']
  }, {
    icon: MailIcon,
    title: 'Email',
    lines: ['info@aucommission.org', 'procurement@aucommission.org']
  }, {
    icon: ClockIcon,
    title: 'Office Hours',
    lines: ['Monday – Friday: 09:00 – 17:30 GMT', 'Saturday – Sunday: Closed', 'Public Holidays: Closed']
  }];
  return <div data-id="element-674" data-mp="frt4gaahz749">
      <PageHero title="Contact Us" subtitle="Get in touch with the African Union Commission" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Contact'
    }]} data-id="element-675" data-mp="fq8cmsfki2a3" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-676" data-mp="2hgz1758koh6m">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-677" data-mp="2hia2myejpcuk">
          {contactInfo.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-678" data-mp="3a5nuwj7atk8c" data-mpi={index}>
                <Card className="h-full" data-id="element-679" data-mp="1pohuyibxozn2">
                  <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center mb-4" data-id="element-680" data-mp="1prnj9vcqnxhq">
                    <Icon size={24} className="text-au-green" data-id="element-681" data-mp="29xr4uflpd0ie" />
                  </div>
                  <h3 className="font-bold text-charcoal mb-3" data-id="element-682" data-mp="257ar0udyzcvi">{item.title}</h3>
                  <div className="space-y-1" data-id="element-683" data-mp="2557r9in0m5dl">
                    {item.lines.map((line, i) => <p key={i} className="text-sm text-gray-600" data-id="element-684" data-mp="2064xiuchjlkx" data-mpi={i}>
                        {line}
                      </p>)}
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="grid lg:grid-cols-2 gap-8" data-id="element-685" data-mp="2nxilyp7imx0m">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-686" data-mp="2nvz8t7h5p232">
            <Card data-id="element-687" data-mp="17l3jpcqa76fg">
              <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-688" data-mp="17h7egp0g9kr4">
                Send Us a Message
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-689" data-mp="3f5kawopziial"></div>

              {submitted ? <div className="py-12 text-center" data-id="element-690" data-mp="3j6vsq82m9z88">
                  <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-691" data-mp="3j7f7xh0vu9ik">
                    <CheckCircleIcon size={32} className="text-au-green" data-id="element-692" data-mp="3o3vzkl7aj60e" />
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-693" data-mp="3sys1qe68r9r8">
                    Message Sent
                  </h3>
                  <p className="text-gray-600" data-id="element-694" data-mp="6p8o0kfa1c7w">
                    Thank you for reaching out. We'll respond within 2 business
                    days.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-695" data-mp="fqf8t5oyl96y">
                  {/* Honeypot anti-spam field — hidden from real users */}
                  <input type="checkbox" name="botcheck" className="hidden" style={{
                display: 'none'
              }} tabIndex={-1} aria-hidden="true" data-id="element-696" data-mp="2ckjqvguu2ovf" />
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-697" data-mp="3tt49ir1vqu17">
                    <div data-id="element-698" data-mp="3tup4ghs3wezl">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-699" data-mp="3tqrk5stslhe4">
                        Full Name <span className="text-au-red" data-id="element-700" data-mp="3owdvsadjsaiy">*</span>
                      </label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Your full name" data-id="element-701" data-mp="7jt7wmz7rxs0" />
                    </div>
                    <div data-id="element-702" data-mp="19y756kp23rmq">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-703" data-mp="19yql5vv5c5y4">
                        Email <span className="text-au-red" data-id="element-704" data-mp="v1g5097k6bw2">*</span>
                      </label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="you@email.com" data-id="element-705" data-mp="zzre2hq14id8" />
                    </div>
                  </div>

                  <div data-id="element-706" data-mp="7a2du6l58mco">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-707" data-mp="7dgcp4ulmdfd">
                      Organization
                    </label>
                    <input type="text" value={formData.organization} onChange={e => setFormData({
                  ...formData,
                  organization: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Company or organization" data-id="element-708" data-mp="iribgtmf1yo8" />
                  </div>

                  <div data-id="element-709" data-mp="3m8qsxcd8ewif">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-710" data-mp="3m9jr3oy4kdoy">
                      Inquiry Type <span className="text-au-red" data-id="element-711" data-mp="3h8chdkcf94fn">*</span>
                    </label>
                    <select required value={formData.inquiryType} onChange={e => setFormData({
                  ...formData,
                  inquiryType: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-712" data-mp="3d7k84r3juzjd">
                      <option value="" data-id="element-713" data-mp="192alwo5dz1co">Select inquiry type</option>
                      <option value="procurement" data-id="element-714" data-mp="18zxy3ucy5wrp">Procurement & Bids</option>
                      <option value="partnership" data-id="element-715" data-mp="1e155hsb6gtxu">
                        Partnership Opportunities
                      </option>
                      <option value="projects" data-id="element-716" data-mp="1nqopj1ammtua">Projects & Programmes</option>
                      <option value="media" data-id="element-717" data-mp="1nsrnc7i0tr9k">Media & Press</option>
                      <option value="general" data-id="element-718" data-mp="qahuuz9vnq7l">General Inquiry</option>
                    </select>
                  </div>

                  <div data-id="element-719" data-mp="v8jeblb7trjl">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-720" data-mp="v81bg5u1sf2p">
                      Subject <span className="text-au-red" data-id="element-721" data-mp="ozyg3h1fnv21">*</span>
                    </label>
                    <input type="text" required value={formData.subject} onChange={e => setFormData({
                  ...formData,
                  subject: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Brief subject line" data-id="element-722" data-mp="tyrxhkjuc67h" />
                  </div>

                  <div data-id="element-723" data-mp="ky3hikavzvry">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-724" data-mp="3jm0n2ni19mnd">
                      Message <span className="text-au-red" data-id="element-725" data-mp="3ol3uufpee6z1">*</span>
                    </label>
                    <textarea required rows={6} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green resize-none" placeholder="How can we help you?" data-id="element-726" data-mp="3oir2d8m0ke7q" />
                  </div>

                  {error && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-727" data-mp="1dy0ajgmzq85j">
                      <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-728" data-mp="1919ugtb6rck4" />
                      <span data-id="element-729" data-mp="z10t712c70xv">{error}</span>
                    </div>}

                  <Button type="submit" variant="primary" disabled={submitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-730" data-mp="pdwy3p60xobl">
                    <SendIcon size={18} data-id="element-731" data-mp="gea3pnbppis1" />
                    {submitting ? 'Sending…' : 'Send Message'}
                  </Button>
                </form>}
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} className="space-y-6" data-id="element-732" data-mp="185pmt7e28ndi">
            <Card className="p-0 overflow-hidden" data-id="element-733" data-mp="13c4zqae09dt3">
              <div className="aspect-square bg-au-green-light relative" data-id="element-734" data-mp="1r3io6591cp8s">
                <iframe title="African Union Commission Office Location" src="https://www.openstreetmap.org/export/embed.html?bbox=-0.1970%2C5.5560%2C-0.1830%2C5.5680&layer=mapnik&marker=5.5620%2C-0.1900" className="w-full h-full border-0" loading="lazy" data-id="element-735" data-mp="1r1fpyxxqbpsz"></iframe>
              </div>
              <div className="p-6" data-id="element-736" data-mp="3q9o5htrb5buy">
                <div className="flex items-start gap-3" data-id="element-737" data-mp="3lb4evg1rrbw9">
                  <MapPinIcon size={20} className="text-au-green flex-shrink-0 mt-1" data-id="element-738" data-mp="3lbmjp12hyyfs" />
                  <div data-id="element-739" data-mp="2smp2z2dxp9p5">
                    <h3 className="font-bold text-charcoal mb-1" data-id="element-740" data-mp="2sn8oet2smg7x">
                      Visit Our Office
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-741" data-mp="37laqeyu6etn2">
                      Liberia Road, Ambassadorial Enclave
                      <br data-id="element-742" data-mp="32mps54yhik19" />
                      Accra, Ghana
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-au-green text-white border-au-green" data-id="element-743" data-mp="htmajil7gn0l">
              <h3 className="text-xl font-serif mb-2" data-id="element-744" data-mp="2nj7q4bdce8y8">Procurement Inquiries</h3>
              <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-745" data-mp="2shrhwsemaeyi"></div>
              <p className="text-au-green-light mb-4 text-sm" data-id="element-746" data-mp="2si17ievr2k3o">
                For all bid, tender, and contract-related inquiries, please
                contact our procurement team directly for the fastest response.
              </p>
              <div className="space-y-2 text-sm" data-id="element-747" data-mp="36hk5wfyreh2s">
                <div className="flex items-center gap-2" data-id="element-748" data-mp="36h0w5mj9iyzu">
                  <MailIcon size={16} data-id="element-749" data-mp="3bg3qabxpfkt1" />
                  <a href="mailto:procurement@aucommission.org" className="hover:text-au-gold transition-colors" data-id="element-750" data-mp="3bf2e244ycs9q">
                    procurement@aucommission.org
                  </a>
                </div>
                <div className="flex items-center gap-2" data-id="element-751" data-mp="3p8jw1khzpwxn">
                  <PhoneIcon size={16} data-id="element-752" data-mp="3p5xhh0womh5x" />
                  <span data-id="element-753" data-mp="3u6lcryry1k5y">+233 50 753 7362</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>;
}